import React, { useMemo, useState } from 'react'
import { Stack, Typography, Box, IconButton, useTheme, useMediaQuery } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/client'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import TemporaryIcon from 'components/global/TemporaryIcon'
import { useSites } from 'store'
import { useGetStatsTimeRange } from '../store'
import { convertDateRange } from '../hooks/use-traffic'
import { OPERATION_FIND_USERS_LANGUAGE } from '../../operations-gql/queries'
import TopLanguagesWidgetSkeleton from '../../Skeletons/TopLanguagesWidgetSkeleton'
import WidgetContainer from './WidgetContainer'

const TopLanguagesWidget = () => {
  const { t, i18n } = useTranslation()
  const [{ selected }] = useSites()
  const [selectedTimeRange] = useGetStatsTimeRange()
  const [currentSlide, setCurrentSlide] = useState(0)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.down('md'))
  const isLargeDesktop = useMediaQuery(theme.breakpoints.up('xl'))
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))
  const { startsAt, endsAt } = convertDateRange(selectedTimeRange)

  const { loading, error, data } = useQuery(OPERATION_FIND_USERS_LANGUAGE, {
    variables: {
      site_sf_ids: selected ? selected.map(({sf_id}) => sf_id) : [],
      created_at_start: startsAt,
      created_at_end: endsAt,
    },
  })

  const allLanguages = useMemo(() => {
    if (!data?.usersLanguage?.length) return []
    
    return data.usersLanguage
      .map(({ key, percentage }) => {
        const languageNames = new Intl.DisplayNames([i18n.language], {
          type: 'language',
        })
        try {
          return {
            language: languageNames.of(key),
            percentage: percentage.toFixed(1)
          }
        } catch (e) {
          console.error(e)
        }
      })
      .filter(Boolean)
  }, [data, i18n.language])

  const slidesToShow = useMemo(() => {
    if (isMobile) return 1
    if (isTablet) return 2
    if (isDesktop && !isLargeDesktop) return 3
    if (isLargeDesktop) return 4
    return 3
  }, [isMobile, isTablet, isDesktop, isLargeDesktop])

  const CustomPrevArrow = (props) => {
    const { onClick } = props
    const isDisabled = currentSlide === 0
    
    return (
      <IconButton
        onClick={onClick}
        disabled={isDisabled}
        sx={{
          position: 'absolute',
          left: '-30px',
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 1,
          width: '24px',
          height: '24px',
          padding: 0,
          minWidth: 'auto',
          color: isDisabled ? 'rgba(101, 101, 101, 0.3)' : '#656565',
          backgroundColor: 'transparent !important',
          '&:hover': {
            backgroundColor: 'transparent !important',
            color: isDisabled ? 'rgba(101, 101, 101, 0.3)' : '#333333',
          },
          '&.Mui-disabled': {
            color: 'rgba(101, 101, 101, 0.3)',
          }
        }}
      >
        <Box sx={{ transform: 'rotate(90deg)' }}>
          <TemporaryIcon iconlabel="chevron-down" iconsize="16px" />
        </Box>
      </IconButton>
    )
  }

  const CustomNextArrow = (props) => {
    const { onClick } = props
    const isDisabled = currentSlide >= allLanguages.length - slidesToShow
    
    return (
      <IconButton
        onClick={onClick}
        disabled={isDisabled}
        sx={{
          position: 'absolute',
          right: '-30px',
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 1,
          width: '24px',
          height: '24px',
          padding: 0,
          minWidth: 'auto',
          color: isDisabled ? 'rgba(101, 101, 101, 0.3)' : '#656565',
          backgroundColor: 'transparent !important',
          '&:hover': {
            backgroundColor: 'transparent !important',
            color: isDisabled ? 'rgba(101, 101, 101, 0.3)' : '#333333',
          },
          '&.Mui-disabled': {
            color: 'rgba(101, 101, 101, 0.3)',
          }
        }}
      >
        <Box sx={{ transform: 'rotate(-90deg)' }}>
          <TemporaryIcon iconlabel="chevron-down" iconsize="16px" />
        </Box>
      </IconButton>
    )
  }

  const sliderSettings = {
    dots: false,
    infinite: false,
    speed: 300,
    slidesToShow: Math.min(slidesToShow, allLanguages.length),
    slidesToScroll: 1,
    autoplay: false,
    arrows: true,
    swipeToSlide: true,
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    beforeChange: (oldIndex, newIndex) => {
      setCurrentSlide(newIndex)
    }
  }

  return (
    <WidgetContainer
      width={{ xs: '100%', sm: '50%', md: '45%' }}
      minHeight="120px"
      loading={loading}
      LoadingComponent={TopLanguagesWidgetSkeleton}
      error={!!error}
      isEmpty={!allLanguages.length}
      emptyIcon="language"
      emptyMessage={error ? t('stats.traffic.widgets.top_languages.error') : t('stats.traffic.widgets.top_languages.no_data')}
    >
      <Stack spacing={1} alignItems="center" sx={{ width: '100%' }}>
        <Stack spacing={0.5} alignItems="center" pb={1}>
          <TemporaryIcon 
            iconlabel="language" 
            iconsize="16px"
            iconcolor="#1976d2"
          />
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '12px',
              textTransform: 'uppercase',
              textAlign: 'center'
            }}
          >
            {t('stats.traffic.widgets.languages.title')}
          </Typography>
        </Stack>
        
        <Box sx={{ 
          width: '100%', 
          maxWidth: '600px',
          px: 4
        }}>
          <Slider {...sliderSettings}>
            {allLanguages.map((item, index) => (
              <Box key={index} sx={{ px: 1 }}>
                <Stack 
                  direction="row" 
                  alignItems="center" 
                  spacing={0.5} 
                  sx={{ 
                    justifyContent: 'center',
                    height: '60px',
                    px: 1
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '32px',
                      fontWeight: 275,
                      color: 'text.primary',
                      lineHeight: 1
                    }}
                  >
                    {item.percentage}
                  </Typography>
                  <Stack alignItems="flex-start" spacing={0} sx={{ justifyContent: 'space-between', height: '28px' }}>
                    <Typography
                      sx={{
                        fontSize: '10px',
                        fontWeight: 400,
                        color: 'text.disabled',
                        lineHeight: 1
                      }}
                    >
                      %
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: 400,
                        color: 'text.secondary',
                        lineHeight: 1.2,
                        maxWidth: '70px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {item.language}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Slider>
        </Box>
      </Stack>
    </WidgetContainer>
  )
}

export default TopLanguagesWidget